import { GoogleGenerativeAI } from "@google/generative-ai";

// Initialize the Gemini AI with API key from environment variables
const initializeGemini = () => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  
  if (!apiKey) {
    console.error("Gemini API key not found. Please add REACT_APP_GEMINI_API_KEY to your .env file");
    return null;
  }
  
  return new GoogleGenerativeAI(apiKey);
};

/**
 * Generate AI response for legal queries using Google Gemini
 * @param {string} query - The user's legal question
 * @param {string} lawyerType - Type of lawyer (e.g., 'Family Lawyer', 'Criminal Defense')
 * @returns {Promise<string>} - AI-generated response
 */
export const generateLegalResponse = async (query, lawyerType = 'General') => {
  try {
    console.log("Generating legal response...");
    console.log("Lawyer Type:", lawyerType);
    const genAI = initializeGemini();
    
    if (!genAI) {
      return "⚠️ AI service is not configured. Please contact the administrator to set up the Gemini API key.";
    }

    // Use Gemini Pro model (free tier)
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

    // Create a comprehensive prompt for legal advice
    const prompt = `You are an AI legal assistant specialized in Pakistani law, with expertise in the Pakistan Penal Code (PPC) and Pakistani Constitution.

**Lawyer Type:** ${lawyerType}

**User's Legal Query:**
${query}

**Instructions:**
- Provide a clear, professional legal response based on Pakistani law
- Reference relevant sections of the PPC or Constitution when applicable
- Keep the response concise but informative (200-300 words)
- Use simple language that non-lawyers can understand
- If the query is outside Pakistani jurisdiction, mention that
- Always remind users to consult with a licensed lawyer for official legal advice
- Be empathetic and professional in your tone

Please provide your legal guidance:`;

    // Generate content
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();

    return text || "I apologize, but I couldn't generate a response. Please try rephrasing your question.";

  } catch (error) {
    console.error("Error generating AI response:", error);
    
    // Handle specific errors
    if (error.message && error.message.includes("API key")) {
      return "⚠️ API key error. Please ensure your Gemini API key is correctly configured.";
    }
    
    if (error.message && error.message.includes("quota")) {
      return "⚠️ API quota exceeded. Please try again later or contact the administrator.";
    }

    return "⚠️ Sorry, I encountered an error while processing your query. Please try again in a moment.";
  }
};

/**
 * Check if Gemini API is configured
 * @returns {boolean}
 */
export const isGeminiConfigured = () => {
  return !!process.env.REACT_APP_GEMINI_API_KEY;
};

